/**
 * useTaskTabActions — the create handler for a record's Tasks tab and the
 * slideout Tasks section, shared between the list toolbar's "Add task" button
 * and the list's empty-state "Create your first task" button, so the composer
 * opens with identical linked-entity context from either entry point.
 *
 * A thin binding of `useOpenTaskCompose` to one host record — permission,
 * default stage and compose-call shape all live there, so a task created from
 * a record behaves exactly like one created from the calendar or the quick
 * draft menu. The twin of `useNoteTabActions`.
 */

import { useCallback, useMemo } from 'react';

import { useOpenTaskCompose } from './useOpenTaskCompose';

export interface TaskTabActions {
  /** Open the composer for a task pre-linked to the host record. */
  startCreatingTask: () => void;
  /** Whether the current user may create tasks on this record. */
  canCreateTask: boolean;
  /**
   * Whether the default task stage is still resolving (pipelines loading).
   * While true, record task entry points should keep the "Add task" affordance
   * disabled rather than open an unstaged task — the stage may resolve to "To
   * Do" a moment later. Forwarded from useOpenTaskCompose so a record's Tasks
   * tab / section gate exactly as QuickDraftMenu does.
   */
  isStageLoading: boolean;
}

export function useTaskTabActions(
  entityId: string,
  hostEntityType: string
): TaskTabActions {
  const { openTaskCompose, canCreateTask, isStageLoading } =
    useOpenTaskCompose();

  const startCreatingTask = useCallback(() => {
    // Belt-and-suspenders: entry points disable the affordance while the stage
    // is still resolving, but guard here too so a stage still loading can never
    // open an unstaged task — including via an empty-state create that isn't
    // itself disabled (mirrors QuickDraftMenu's handler guard).
    if (isStageLoading) {
      return;
    }
    openTaskCompose({
      linkedEntities: [{ entityId, entityTypeName: hostEntityType }],
    });
  }, [openTaskCompose, entityId, hostEntityType, isStageLoading]);

  return useMemo(
    () => ({ startCreatingTask, canCreateTask, isStageLoading }),
    [startCreatingTask, canCreateTask, isStageLoading]
  );
}
